import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSnackbar } from 'notistack'

import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, CircularProgress, Tab, Typography } from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined'
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { FormProvider, RHFFileField } from '../../common/hook-form'
import { apiConstants, postMultipart } from '../../common/constants'
import PageTitle from '../../components/PageTitle'

import LabelDownloadBulk from './LabelDownloadBulks'
import {
  PageWrapper,
  SectionCard,
  SectionHeader,
  SectionIconBox,
  SectionTitle,
  SectionSubtitle,
  SectionBody,
  RunBtn,
  ResetBtn,
  ResultBar,
  DownloadChip,
  InfoBanner,
} from './styled'

const SAMPLE_CSV = 'https://djslb35m04okm.cloudfront.net/generate_labels.csv'

/**
 * D2C Bulk Label Download — two distinct workflows in tabs:
 *   • Tab 1 "By Pincode": filter by date + pincode, backend finds matching
 *     orders and returns a merged labels PDF.
 *   • Tab 2 "By AWB List": upload an XLSX of specific AWBs, get an excel +
 *     zip of labels + merged PDF for exactly those AWBs.
 */
const BulkDownloadLabelsB2C = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [tab, setTab] = useState('filter')

  // Upload form (only used on the "By AWB List" tab)
  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        orderFile: Yup.mixed().required('Please select a file'),
      })
    ),
    defaultValues: { orderFile: null },
  })
  const { handleSubmit, reset } = methods

  const [excelLink, setExcelLink] = useState('')
  const [zipLink, setZipLink] = useState('')
  const [mergedPdf, setMergedPdf] = useState('')
  const [isUploading, setIsUploading] = useState(false)

  const hasResults = excelLink || zipLink || mergedPdf

  const onSubmit = async (data) => {
    setIsUploading(true)
    try {
      const form = new FormData()
      form.append('file', data.orderFile[0])
      const response = await postMultipart(
        `${apiConstants.BULK_DOWNLOAD_LABELS}?shipmentType=B2C`,
        form
      )
      if (response?.data?.excel?.startsWith('https')) {
        setExcelLink(response.data.excel)
        setZipLink(response.data.zipFile || '')
        setMergedPdf(response.data.mergedPdf || '')
        enqueueSnackbar('Successfully uploaded!', { variant: 'success' })
        reset({ orderFile: null })
      } else {
        enqueueSnackbar(response?.data?.[3962] || 'Upload failed', { variant: 'error' })
      }
    } catch (err) {
      console.error(err)
      const message = err?.response?.data?.message || err.message
      enqueueSnackbar(message || 'Error uploading file', { variant: 'error' })
    } finally {
      setIsUploading(false)
    }
  }

  const handleClearResults = () => {
    reset({ orderFile: null })
    setExcelLink('')
    setZipLink('')
    setMergedPdf('')
  }

  return (
    <>
      <PageTitle>Shipping Labels Download (B2C)</PageTitle>
      <PageWrapper>
        <TabContext value={tab}>
          <Box sx={{ backgroundColor: '#fff', borderRadius: '16px 16px 0 0', border: '1px solid #eee', borderBottom: 'none' }}>
            <TabList
              onChange={(_, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              sx={{
                '& .MuiTab-root': {
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  minHeight: 48,
                },
              }}
            >
              <Tab
                value="filter"
                icon={<TuneIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="By Pincode + Date"
              />
              <Tab
                value="upload"
                icon={<FileUploadOutlinedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="By AWB List (Upload File)"
              />
            </TabList>
          </Box>

          <TabPanel value="filter" sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <InfoBanner>
                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption">
                  Filter all orders by pickup pincode and date — we generate a single merged PDF
                  of every matching label.
                </Typography>
              </InfoBanner>
              <LabelDownloadBulk />
            </Box>
          </TabPanel>

          <TabPanel value="upload" sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <InfoBanner>
                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption">
                  Already have specific AWBs in mind? Upload an XLSX of up to 500 AWBs and we'll
                  return the labels excel, a ZIP of individual PDFs, and a merged PDF.
                </Typography>
              </InfoBanner>

              <SectionCard>
                <SectionHeader>
                  <SectionIconBox tone="warning">
                    <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
                  </SectionIconBox>
                  <SectionTitle>Upload AWBs</SectionTitle>
                  <SectionSubtitle>XLSX • Up to 500 AWBs per upload</SectionSubtitle>
                </SectionHeader>
                <SectionBody>
                  <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    <RHFFileField name="orderFile" accept={['xlsx']} />
                    <ResultBar>
                      <RunBtn
                        type="submit"
                        variant="contained"
                        color="warning"
                        size="small"
                        disabled={isUploading}
                        startIcon={
                          isUploading ? (
                            <CircularProgress size={14} sx={{ color: '#fff' }} />
                          ) : (
                            <FileUploadOutlinedIcon fontSize="small" />
                          )
                        }
                      >
                        {isUploading ? 'Uploading…' : 'Upload & Generate'}
                      </RunBtn>
                      <DownloadChip
                        tone="primary"
                        href={SAMPLE_CSV}
                        download="generate_labels"
                        startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 14 }} />}
                      >
                        Sample CSV
                      </DownloadChip>
                      {hasResults && (
                        <ResetBtn variant="outlined" color="inherit" size="small" onClick={handleClearResults}>
                          Clear
                        </ResetBtn>
                      )}
                    </ResultBar>

                    {hasResults && (
                      <ResultBar>
                        {excelLink && (
                          <DownloadChip
                            tone="success"
                            href={excelLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<TableViewOutlinedIcon sx={{ fontSize: 14 }} />}
                          >
                            Download Excel
                          </DownloadChip>
                        )}
                        {zipLink && (
                          <DownloadChip
                            tone="success"
                            href={zipLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<FolderZipOutlinedIcon sx={{ fontSize: 14 }} />}
                          >
                            Download ZIP
                          </DownloadChip>
                        )}
                        {mergedPdf && (
                          <DownloadChip
                            tone="success"
                            href={mergedPdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 14 }} />}
                          >
                            Download Merged PDF
                          </DownloadChip>
                        )}
                      </ResultBar>
                    )}
                  </FormProvider>
                </SectionBody>
              </SectionCard>
            </Box>
          </TabPanel>
        </TabContext>
      </PageWrapper>
    </>
  )
}

export default BulkDownloadLabelsB2C
